/**
 PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA

 *  Copyright © 2017 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */
import React from 'react';
import ReactDOM from 'react-dom';
import MathUtil from './MathUtil';
import TextRenderer from './TextRenderer';

/**
 * A class to keep static utility metjods.
 *
 * @author Hari Gangadharan
 */

export default class Utilities {
  /**
   * RTL languages list
   */
  static rtlLanguages = ['ar'];

  /**
   * Formats the time in 0:00 format from time in seconds
   *
   * @param time the time in seconds
   * @returns {string} formatted time
   */
  static formatTime(time, prefix = false) {
    let minutes = Math.floor(time / 60);
    let seconds = Math.floor(time % 60);
    seconds = (seconds >= 10) ? seconds : `0${seconds}`;
    if (prefix) {
      minutes = (minutes >= 10) ? minutes : `0${minutes}`;
    }
    return `${minutes}:${seconds}`;
  }

  /**
   * Formats the time in 00:00:000 format from time in seconds
   *
   * @param time the time in seconds
   * @returns {string} formatted time with milliseconds
   */
  static formatTimeWithMilliseconds(time, prefix = false) {
    let minutes = Math.floor(time / 60);
    let seconds = Math.floor(time % 60);
    let milliseconds = '000';
    seconds = (seconds >= 10) ? seconds : `0${seconds}`;
    if (prefix) {
      minutes = (minutes >= 10) ? minutes : `0${minutes}`;
    }
    if (Math.floor(time) !== time) { // check if any milliseconds data (decimal)
      const temp = time.toFixed(3);
      [, milliseconds] = temp.split('.');
    }
    return `${minutes}:${seconds}:${milliseconds}`;
  }

  /**
   * Check if the media object contains image
   *
   * @param {object} media
   * @returns {boolean} true / false
   */
  static isImage(media) {
    return (media && Object.keys(media).length > 0 && media.type === 'image');
  }

  /**
   * Converts the time in seconds from time in milliseconds
   *
   * @param time the time in milliseconds
   * @returns {number} the in seconds
   */
  static formatMillisecondsToSeconds(time) {
    if (time < 1000) {
      return time > 500 ? 1 : 0;
    }
    return (Math.floor(time / 60000) * 60) + Math.floor((time / 1000) % 60);
  }

  /**
   * Converts the time in milliseconds from time in seconds
   *
   * @param time the time in seconds
   * @returns {number} the time in milliseconds
   */
  static formatSecondsToMilliSeconds(time) {
    return (time * 1000);
  }

  /**
   * Formats the time in 0:00 format from time in milliseconds
   *
   * @param time the time in milliseconds
   * @returns {string} formatted time
   */
  static formatMillisecondsToTime(time) {
    if (time < 1000) {
      return '0:01';
    }
    return Utilities.formatTime(Utilities.formatMillisecondsToSeconds(time));
  }

  /**
   * Check the URL is from youtube
   *
   * @param {string} url to be validate
   * @returns {string} youtube video ID or false for non youtube url
   */
  static youtubeUrl = (url) => {
    // eslint-disable-next-line
    const domainName = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    const matches = url.match(domainName);
    if (matches) {
      return matches[1];
    }
    return false;
  };

  /**
   * Check the events from youtube player
   *
   * @param {number} to be check the event
   * @returns {string} youtube event name or false for unwanted events
   */
  static youtubeAnalyticsAction = (eve) => {
    let YouTubeAction;
    switch (eve) {
      case 0:
        YouTubeAction = 'Finished';
        break;
      case 1:
        YouTubeAction = 'Play';
        break;
      case 2:
        YouTubeAction = 'Pause';
        break;
      default: break;
    }
    if (YouTubeAction) {
      return YouTubeAction;
    }
    return false;
  }

  /**
  * Attach event handler
  */
  static attachHandler = (container, callback, overlay) => {
    if (container) {
      const dataType = container.getAttribute('data-type');
      const element = ((container.children.length > 0 && dataType !== 'popoverLink') ? container.children[0] : container);
      element.style.cursor = 'pointer';
      container.addEventListener('keypress', (eve) => {
        if ((eve.key === ' ') || (eve.key === 'Enter')) {
          callback(eve);
        }
      });
      element.addEventListener('click', (eve) => {
        callback(eve);
      });

      if (overlay) {
        const wrapper = document.createElement('div');
        container.appendChild(wrapper);

        ReactDOM.render(
          overlay,
          wrapper
        );
      }
    }
  }

  /**
   * Check is content type is MathML
  */
  static isMathML(type) {
    return type.toLowerCase() === 'mathml';
    // <MATHML> <mathml>
  }

  /**
   * Loops through images array for getting glossary image
   * and appends image at specified charAt position in glossary defination
   * @param data extracted data from json defination
   * @returns {string} returns string with appended image
  */
  static renderImage = (data) => {
    let result;
    const definitionText = new TextRenderer().escapeHtml(data.text);
    data.images.forEach((image) => {
      const imageDef = `<img class='inlineImageDefinition' src='${image.imagedata.path}' width='${image.imagedata.width}' height='${image.imagedata.height}' alt='${image.imagedata.alttext}'/>`;
      result = definitionText + imageDef;
    });
    return result;
  }

  static getMathMlFromCompliedText = (mathText) => {
    let updatedMath = mathText;
    const parser = new window.DOMParser();
    const parsedHtml = parser.parseFromString(updatedMath, 'text/html');
    const errorNode = parsedHtml.querySelector('parsererror');
    if (!errorNode) {
      const mathMlParsedElement = parsedHtml.querySelectorAll('.mathml-expression');
      if (mathMlParsedElement && mathMlParsedElement.length > 0) {
        mathMlParsedElement.forEach((el) => {
          const dataMathMElement1 = el.querySelector('*[data-mathml]');
          if (dataMathMElement1.getAttribute) {
            const dataMathMl = dataMathMElement1.getAttribute('data-mathml');
            if (updatedMath
              && updatedMath.replace
            ) {
              const parentInnerHTML2 = updatedMath;
              const updatedInnerHTML2 = parentInnerHTML2.replace(el.outerHTML, dataMathMl);
              updatedMath = updatedInnerHTML2;
            }
          }
        });
      }
    }
    return updatedMath;
  }

  static textFromContent = (content, mathJaxVersion = '2.7.0') => {
    const elements = [];
    let dispContent = [];
    if (!Array.isArray(content)) {
      if (typeof content === 'string') {
        dispContent.push({ type: content.includes('</math>') ? 'mathml' : 'text', text: content });
      } else {
        dispContent.push(content);
      }
    } else {
      dispContent = content;
    }
    dispContent.forEach((item, index) => {
      if (item) {
        if (item.type && item.type.toLowerCase() === 'paragraph') {
          elements.push(Utilities.textFromContent(item.content));
        } else if (item.type && Utilities.isMathML(item.type)) {
          elements.push(
            (
              <span
                key={index} // eslint-disable-line react/no-array-index-key
                className="math-text"
                ref={(c) => { MathUtil.renderMath(c, mathJaxVersion); }}
                dangerouslySetInnerHTML={{ __html: item.text }}
              />
            )
          );
        } else {
          // eslint-disable-next-line react/no-array-index-key, react/no-danger
          elements.push((<span key={index} dangerouslySetInnerHTML={{ __html: item.text }} />));
        }
      }
    });
    return (<span key="text-content">{elements}</span>);
  }

  static closestByClassName = (el, className) => {
    let element = el;
    while (!element.classList.contains(className)) {
      element = element.parentNode;
      if (!element) {
        return null;
      }
    }
    return element;
  }

  static isIE = () => {
    const isIEBrowser = ((window.navigator.userAgent.indexOf('MSIE') !== -1)
    || (window.navigator.userAgent.indexOf('Trident/') > -1));
    return isIEBrowser;
  }

  static isEdge = () => (window.navigator.userAgent.indexOf('Edge') !== -1);

  /**
   * Converts the given JSON content with text symantic and mathml to string format
   * @param content WIP data which will hold text, textsemantics and mathml
   * @returns {string} returns formatted string which study tool interactive support
   */
  static formatContentToHtml(content) {
    let htmlContent = '';
    if (content) {
      if ((content.textsemantics && content.textsemantics.length > 0)
      || (content.mathml && content.mathml.length > 0)) {
        htmlContent = new TextRenderer().formatToHtml(content);
      } else if (content.text || content.images) {
        htmlContent = content.images ? this.renderImage(content) : new TextRenderer().escapeHtml(content.text);
      } else {
        htmlContent = typeof content === 'string' ? new TextRenderer().escapeHtml(content) : '';
      }
    }
    return htmlContent;
  }

  /**
   * retrieve the value from nested array/object by giving the path with separetor
   * @param path path of the key pair to get the value (Ex: search.source.definition)
   * @param obj actual object/array to retrieve the value
   * @param separator separator for the path value (Ex: '.' or '-' based on the path string)
   * @returns {string} returns value from the array based on the path
  */
  static getValue = (path, obj, separator = '.') => {
    const properties = Array.isArray(path) ? path : path.split(separator);
    return properties.reduce((prev, curr) => prev && prev[curr], obj);
  }

  /**
   * format the searchData into structured array
   * @param data the object/array to format
   * @mapper the mapper to take the values in the array
   * @returns {array} returns data array with structured format
  */
  static formatSearchData = (data, mapper) => {
    const resData = {};
    resData.count = data[mapper.count];
    const items = data[mapper.searchResults];
    resData.searchResults = [];
    items.forEach((result) => {
      const res = {};
      const type = Utilities.getValue(mapper.type, result);

      res.type = type;
      if (mapper[type]) {
        Object.keys(mapper[type]).forEach((key) => {
          res[key] = Utilities.getValue(mapper[type][key], result);
        });
      }
      resData.searchResults.push(res);
    });

    resData.searchResults.sort((a, b) => b.score - a.score);
    return resData;
  }

  /**
   * convert seconds into 24 format HH:MM:SS
  */
  static secondsTo24 = (time) => {
    const purify = window.parseInt(time, 10);
    const timeFormat = new Date(null);
    timeFormat.setSeconds(Number.isInteger(purify) ? purify : 0);
    return timeFormat.toISOString().substr(11, 8);
  }

  /**
   * Converts the given time format hh:mm:ss:00 to number of seconds
   * @param timestamp string hh:mm:ss:milliseconds
   * @returns {number} returns number of seconds
   */
  static convert24ToSeconds = (timestamp) => {
    if (!(/^\d{2}:\d{2}:\d{2}:\d{1,10}$/.test(timestamp) || /^\d{2}:\d{2}:\d{2}$/.test(timestamp))) {
      return 0;
    }
    const time = timestamp.split(':');
    if (time.length === 4) {
      return ((+time[0] * 3600) + (+time[1] * 60) + +time[2] + Utilities.formatMillisecondsToSeconds(+time[3]));
    }
    return ((+time[0] * 3600) + (+time[1] * 60) + +time[2]);
  }

  /**
   * Add check for keypress for SPACE and ENTER key to handler
   * @param handler function
   * @returns {number} returns function
   */
  static handleKeypressForSPCandENT = (handler, ...args) => {
    if (typeof handler === 'function') {
      return ((handler1, event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          handler1(event, ...args);
        }
      }).bind(this, handler);
    }
    return null;
  }

  /**
   * convert seconds into 24 format 'HH hours MM minutes SS seconds' for ally
  */

  static extractTimeFromSeconds = (totalSecondsArg) => {
    const totalSeconds = window.parseInt(totalSecondsArg, 10);
    const tempSeconds = totalSeconds;
    const tempMinutes = Math.floor(tempSeconds / 60);
    const seconds = (tempSeconds % 60);
    const hours = Math.floor(tempMinutes / 60);
    const minutes = (tempMinutes % 60);
    return {
      hours,
      minutes,
      seconds
    };
  }

  static secondsTo24ForAlly = (time) => {
    const purify = window.parseInt(time, 10);
    const timeFormat = Utilities.extractTimeFromSeconds(purify);
    let timeString;
    timeString = `${timeFormat.hours} hour${timeFormat.hours > 1 ? 's' : ''}`;
    timeString = `${timeFormat.hours > 0 ? timeString : ''} ${timeFormat.minutes} minute${timeFormat.minutes > 1 ? 's' : ''}`;
    timeString = `${timeFormat.minutes > 0 ? timeString : ''} ${timeFormat.seconds} second${timeFormat.seconds > 1 ? 's' : ''}`;
    return timeString;
  }

  /**
   * To handle all potential characters (instead of a "known" list)
   * use the natural escaping of the browser by letting it convert HTML strings to text with this
   * @param str String
   * @returns {string} returns string
   */
  static unescapeHtmlStr = (str) => {
    const elt = document.createElement('span');
    elt.innerHTML = str;
    return elt.innerText;
  }

  static isRTL = currentLocale => Utilities.rtlLanguages.indexOf(currentLocale) !== -1;

  /**
   * Stripping the html and returning text without html
   * @param {*} html
   */
  static stripHtml = (html) => {
    if (html && html.includes('</math>')) {
      const tripedHtml = html.replace(/<\/?[^>]+>/gi, '');
      const triped = tripedHtml.split('&nbsp');
      return triped[0];
    }
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.replace(/<[^>]*>/g, '') || temp.innerText.replace(/<[^>]*>/g, '') || '';
  }
}



// WEBPACK FOOTER //
// ./src/js/util/Utilities.jsx